import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useConfig } from '../context/useConfig';
import { fadeInUp } from '../utils/motionVariants';
import { SafeImage } from './SafeImage';

/*
  Hobbies Section
  Data-driven from config.hobbiesSection:
    heading, subheading, autoplayMs, items: [{ title, image, description, tags[] }]
  Accessible carousel: keyboard arrow navigation, pause on hover/focus, reduced motion compliance.
*/

function Hobbies() {
  const { config, isHydrated } = useConfig();
  const section = config.hobbiesSection || {};
  const items = section.items || [];
  const autoplayMs = section.autoplayMs || 6000;
  const placeholderImage = section.placeholderImage || '/projects/placeholder.svg';
  const [index, setIndex] = useState(0);
  const [isPaused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const idBase = 'hobby-carousel';

  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length]);

  // Autoplay
  useEffect(() => {
    if (!isHydrated || reduceMotion || isPaused || items.length <= 1) return;
    timerRef.current = setTimeout(next, autoplayMs);
    return () => clearTimeout(timerRef.current);
  }, [isHydrated, index, isPaused, reduceMotion, next, autoplayMs, items.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isHydrated || items.length <= 1) return;
    
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isHydrated, next, prev, items.length]);

  // No skeleton needed - we start with fallback config for immediate render
  if (!items.length) return null;

  return (
    <section id="hobbies" className="relative isolate py-28 sm:py-32 bg-gradient-to-b from-cyan-50 via-white to-emerald-50 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_1px_1px,#000_1px,transparent_0)] [background-size:22px_22px]" aria-hidden />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <motion.header initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="max-w-2xl mx-auto text-center mb-16 space-y-6">
          <motion.h2 variants={fadeInUp(0, reduceMotion ? 0 : 24)} className="text-4xl sm:text-5xl font-extrabold tracking-tight gradient-text">{section.heading || 'Hobbies'}</motion.h2>
          {section.subheading && (
            <motion.p variants={fadeInUp(0.1, 0)} className="text-lg text-gray-600 leading-relaxed font-medium">{section.subheading}</motion.p>
          )}
        </motion.header>

        {/* Carousel container - keyboard accessible for navigation */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          className="relative"
          role="region"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          tabIndex={0}
          aria-roledescription="carousel"
          aria-label={section.heading || 'Hobbies carousel'}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          {/* Slides */}
          <div className="relative min-h-[380px] sm:min-h-[440px] md:min-h-[460px] max-h-[90vh] flex items-center justify-center pb-4">
            <AnimatePresence initial={false} mode="wait">
              <motion.article
                key={index}
                className="relative w-full max-w-4xl mx-auto grid md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 items-center p-6 sm:p-8 md:p-10 rounded-3xl bg-white/70 backdrop-blur-xl ring-1 ring-emerald-100 shadow-lg overflow-hidden"
                initial={{ opacity: 0, x: reduceMotion ? 0 : 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: reduceMotion ? 0 : -60 }}
                transition={{ duration: 0.65, ease: 'easeOut' }}
                aria-labelledby={`${idBase}-title-${index}`}
                aria-describedby={`${idBase}-desc-${index}`}
              >
                {/* Image */}
                <div className="relative group flex-shrink-0">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-200/40 via-transparent to-cyan-200/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <SafeImage
                    src={items[index].image}
                    fallbackSrc={placeholderImage}
                    alt={items[index].title}
                    loading="lazy"
                    className="w-full h-56 sm:h-64 md:h-72 object-cover rounded-2xl shadow-sm ring-1 ring-emerald-100/50"
                  />
                </div>
                {/* Text */}
                <div className="flex flex-col h-full min-h-0">
                  <h3 id={`${idBase}-title-${index}`} className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight mb-3 sm:mb-4">
                    {items[index].title}
                  </h3>
                  <p id={`${idBase}-desc-${index}`} className="text-sm text-gray-600 leading-relaxed mb-4 sm:mb-5">
                    {items[index].description}
                  </p>
                  {items[index].tags?.length > 0 && (
                    <ul className="flex flex-wrap gap-2 mb-4 sm:mb-6" aria-label="Tags">
                      {items[index].tags.map((tag, i) => (
                        <li key={i} className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                          {tag}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto flex items-center gap-3 text-[11px] font-medium text-gray-400">
                    <span>{index + 1} / {items.length}</span>
                    {section.autoplayMs && !reduceMotion && (
                      <span className="flex-1 h-1 bg-gray-200 rounded overflow-hidden relative">
                        <motion.span
                          key={index + '-progress'}
                          className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400"
                          initial={{ width: 0 }}
                          animate={{ width: isPaused ? 0 : '100%' }}
                          transition={{ duration: (autoplayMs / 1000), ease: 'linear' }}
                          aria-hidden
                        />
                      </span>
                    )}
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="mt-6 sm:mt-8 flex items-center justify-center gap-4 sm:gap-6 relative z-10">
            <button
              type="button"
              onClick={prev}
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white shadow hover:shadow-md ring-1 ring-gray-200 flex items-center justify-center text-gray-600 hover:text-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 flex-shrink-0"
              aria-label="Previous hobby"
            >
              <i className="fas fa-arrow-left text-sm" />
            </button>
            <div className="flex items-center gap-2 flex-wrap justify-center" aria-hidden>
              {items.map((_, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    i === index ? 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 w-6' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={next}
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white shadow hover:shadow-md ring-1 ring-gray-200 flex items-center justify-center text-gray-600 hover:text-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 flex-shrink-0"
              aria-label="Next hobby"
            >
              <i className="fas fa-arrow-right text-sm" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hobbies;
